// "Anfrage für Ange.Bot" in the deal detail (spec 8a): positions with the required fields, traffic light and
// evidence, our rules (no-go, minimum batch), the draft query when something is missing, corrections by hand, the
// push to Ange.Bot (M9) and what Ange.Bot reports back.
import {
  belegOf,
  checkPosition,
  completeForAngeBot,
  FIELD_LABELS,
  leistungOf,
  MENGENARTEN,
  OPTIONAL_FIELDS,
  PRODUKTARTEN,
  readyForAngeBot,
  vpeValid,
  type Chargenvorgabe,
  type Evidence,
  type FieldState,
  type Mengenart,
  type Position,
  type Requirements,
  type RuleSet,
} from "@pipeline/core";
import type { AngeBotState, Deal, Frage, MachbarkeitVerdict } from "../api";
import { loadDealDetail, render, S, write } from "../state";
import { esc, fmtDate, fmtDT, inputNumber, parseDeNumber, toast } from "../util";
import { downloadAttachment, showOriginal } from "./original";
import { confirmPush } from "./push-confirm";

const STATE_TXT: Record<FieldState, string> = { ok: "belegt", pruefen: "ohne Beleg, prüfen", fehlt: "fehlt (Pflicht)", offen: "nicht genannt" };
let editing = false;
/** What the form was opened on: the deal's version and requirements then, so saving never overwrites a change made since. */
let editBase: { dealId: string; version: number; requirements: Requirements } | null = null;

/** A newly opened deal starts without an open form. */
export const resetRequirementsEdit = () => {
  editing = false;
  editBase = null;
};

const rules = (): RuleSet => ({
  noGo: S.meta?.rules.noGo ?? [],
  minBatchKg: S.meta?.rules.minBatchKg ?? 50,
  defaultFillMg: S.meta?.rules.defaultFillMg ?? {},
  packOnly: S.meta?.angeBotPackOnly ?? false,
  chargenvorgabe: S.meta?.angeBotChargenvorgabe ?? false,
});
/** Ange.Bot takes the batch size: it is shown and asked for (setting ange_bot_chargenvorgabe). */
const asksChargenvorgabe = () => S.meta?.angeBotChargenvorgabe === true;
const dot = (s: FieldState) => `<span class="st st-${s}" title="${STATE_TXT[s]}" aria-label="${STATE_TXT[s]}"></span>`;

function evidenceLink(e: Evidence | null): string {
  if (!e) return "";
  const title = e.quote ? ` title="„${esc(e.quote)}“"` : "";
  if (e.kind === "mail") {
    const m = S.dealMails.find((x) => x.id === e.id);
    return `<button class="link ev" data-ev-mail="${esc(e.id)}"${title}>Mail ${m ? fmtDate(m.receivedAt) : ""}</button>`;
  }
  if (e.kind === "attachment") {
    const a = S.dealMails.flatMap((m) => m.attachments).find((x) => x.id === e.id);
    // A long file name is cut in the column; the tooltip names it whole.
    const tip = ` title="${esc(a?.filename ?? "Anhang")}${e.quote ? ` – „${esc(e.quote)}“` : ""}"`;
    return `<button class="link ev" data-ev-att="${esc(e.id)}" data-name="${esc(a?.filename ?? "anhang")}"${tip}>📎 ${esc(a?.filename ?? "Anhang")}</button>`;
  }
  const n = S.dealActivities.find((x) => x.id === e.id);
  return `<span class="ev muted"${title}>Notiz ${n ? fmtDate(n.at) : ""}</span>`;
}

function row(label: string, value: string, s: FieldState, e: Evidence | null, manual?: boolean): string {
  // Evidence only next to a value: a source without a value says nothing.
  return `<tr><th>${dot(s)} ${label}</th><td>${value ? esc(value) : `<span class="muted">–</span>`}${manual ? ` <span class="pill p-acc">von Hand</span>` : ""}</td><td>${value ? evidenceLink(e) : ""}</td></tr>`;
}

const LEISTUNG_TXT = { herstellen: "Herstellen", nur_verpacken: "Nur verpacken" } as const;
const MENGENART_TXT: Record<Mengenart, string> = { bestellung: "Bestellung (einmal geliefert)", jahresbedarf: "Jahresbedarf" };
const PORTION_UNITS = ["Stück", "g", "mg"] as const;
const amountText = (q: { amount: number | null; unit: string | null } | undefined) => (typeof q?.amount === "number" ? `${q.amount.toLocaleString("de-DE")} ${q.unit ?? ""}` : "");

/** "400–600 kg je Charge, 3 Chargen je Kampagne (Zulassung)", "keine Vorgabe", or empty while not known. */
export function chargenvorgabeText(c: Chargenvorgabe | undefined): string {
  if (!c || c.vorgegeben === null) return "";
  if (!c.vorgegeben) return "keine Vorgabe";
  const n = (x: number) => x.toLocaleString("de-DE");
  const size =
    c.min !== null && c.max !== null ? (c.min === c.max ? n(c.min) : `${n(c.min)}–${n(c.max)}`) : c.min !== null ? `mindestens ${n(c.min)}` : c.max !== null ? `höchstens ${n(c.max)}` : "Größe?";
  return `${size} ${c.einheit ?? "?"} je Charge${c.chargenJeKampagne !== null ? `, ${n(c.chargenJeKampagne)} Chargen je Kampagne` : ""}${c.quelle ? ` (${c.quelle})` : ""}`;
}

const URTEIL_TXT = { machbar: "machbar", unklar: "unklar", nicht_machbar: "nicht machbar" } as const;
const URTEIL_PILL = { machbar: "p-ok", unklar: "p-info", nicht_machbar: "p-hoch" } as const;

/** Ange.Bot's verdict on the position, marked when the position has changed since. */
/**
 * Ange.Bot's verdict at a position. From machbarkeit/3 on the short sentence first, then what can tip the verdict and
 * what to clarify before the offer, each question marked with who answers it, the customer (clarified in the deal)
 * or production (chat-530, chat-538); neither holds the push up. Before /3 all open points stand under "Offen".
 */
export function verdictHtml(v: MachbarkeitVerdict): string {
  const list = (label: string, xs: string[]) => (xs.length ? `<p class="muted" style="margin:2px 0 0">${label}: ${xs.map(esc).join("; ")}</p>` : "");
  const AN = { kunde: "Kunde", produktion: "Produktion" } as const;
  // The customer's questions first: they are the ones to ask in the deal.
  const questions = (label: string, xs: Frage[] | null) =>
    xs?.length
      ? `<p class="mb-l">${label}</p><ul class="mb-q">${[...xs.filter((f) => f.an === "kunde"), ...xs.filter((f) => f.an === "produktion")]
          .map((f) => `<li><span class="mb-an ${f.an}">${AN[f.an]}</span> ${esc(f.text)}</li>`)
          .join("")}</ul>`
      : "";
  const v3 = v.fragen !== null || v.vorAngebot !== null;
  return `<div class="mb"><span class="pill ${URTEIL_PILL[v.urteil]}">Machbarkeit: ${URTEIL_TXT[v.urteil]}</span> <span class="muted">Ange.Bot, ${fmtDT(v.geprueftAm)}${v.aktuell ? "" : ", vor der letzten Änderung der Position"}</span>
    ${v.kurzsatz ? `<p class="mb-k">${esc(v.kurzsatz)}</p>` : ""}${v.begruendung ? `<p style="margin:4px 0 0">${esc(v.begruendung)}</p>` : ""}${list("Anlagen", v.anlagen)}${v.chargen ? list("Chargen", [v.chargen]) : ""}${
      v3 ? questions("Kann die Machbarkeit kippen", v.fragen) + questions("Vor dem Angebot klären", v.vorAngebot) : list("Offen", v.offen)
    }</div>`;
}

function machbarkeitView(index: number): string {
  const m = S.dealAngeBot?.machbarkeit;
  const v = m?.enabled ? m.positions.find((x) => x.index === index) : undefined;
  return v ? verdictHtml(v) : "";
}

function positionView(p: Position, index: number): string {
  const c = checkPosition(p, rules());
  // Packaging only (F13): no recipe; form and substances are information, the portion is required.
  const packOnly = leistungOf(p) === "nur_verpacken";
  const subs = p.substanzen.length
    ? p.substanzen
        .map((s) => {
          const complete = !!s.name && typeof s.dose === "number" && !!s.unit;
          return `<tr><th>${dot(complete ? (s.evidence || p.substanzenManual ? "ok" : "pruefen") : packOnly ? "pruefen" : "fehlt")} <span class="muted">Substanz</span></th><td>${esc(s.name ?? "?")} ${typeof s.dose === "number" ? esc(s.dose.toLocaleString("de-DE")) : "<b>Dosis?</b>"} ${esc(s.unit ?? "")}</td><td>${evidenceLink(s.evidence)}</td></tr>`;
        })
        .join("")
    : row(FIELD_LABELS.substanzen, "", c.states.substanzen, null);
  return `<div class="req-pos"><div class="req-name"><b>${esc(p.name)}</b>${c.missing.length ? ` <span class="pill p-hoch">Es fehlt: ${esc(c.missing.join(", "))}</span>` : c.blocked.length ? ` <span class="pill p-hoch">${packOnly ? "Menge und Portion klären" : "Menge klären"}</span>` : ` <span class="pill p-ok">vollständig</span>`}</div>
    <table class="req-t">
      ${row(FIELD_LABELS.leistung, LEISTUNG_TXT[leistungOf(p)], c.states.leistung, p.leistung?.evidence ?? null, p.leistung?.manual)}
      ${row(FIELD_LABELS.darreichungsform, p.darreichungsform.value ?? "", c.states.darreichungsform, p.darreichungsform.evidence, p.darreichungsform.manual)}
      ${subs}
      ${row(packOnly ? "Menge der Ware des Kunden" : FIELD_LABELS.menge, amountText(p.menge), c.states.menge, p.menge.evidence, p.menge.manual)}
      ${packOnly ? row(FIELD_LABELS.portion, amountText(p.portion), c.states.portion, p.portion?.evidence ?? null, p.portion?.manual) : ""}
      ${row(FIELD_LABELS.mengenart, p.mengenart?.value ? MENGENART_TXT[p.mengenart.value] : "", c.states.mengenart, p.mengenart?.evidence ?? null, p.mengenart?.manual)}
      ${row("VPE", vpeValid(p.vpe?.value) ? `${p.vpe!.value!.toLocaleString("de-DE")} Stück je Verkaufseinheit` : "", c.states.vpe, p.vpe?.evidence ?? null, p.vpe?.manual)}
      ${OPTIONAL_FIELDS.map((f) => row(FIELD_LABELS[f], p[f]?.value ?? "", c.states[f], p[f]?.evidence ?? null, p[f]?.manual)).join("")}
      ${asksChargenvorgabe() && !packOnly ? row(FIELD_LABELS.chargenvorgabe, chargenvorgabeText(p.chargenvorgabe), c.states.chargenvorgabe, p.chargenvorgabe?.evidence ?? null, p.chargenvorgabe?.manual) : ""}
    </table>
    ${machbarkeitView(index)}
    ${c.batchKg !== null ? `<div class="mass">Chargenmasse ≈ ${c.batchKg.toLocaleString("de-DE", { maximumFractionDigits: 1 })} kg</div>` : ""}
    ${[...c.blocked, ...c.flags].map((f) => `<div class="req-flag">⚠ ${esc(f)}</div>`).join("")}</div>`;
}

function input(name: string, value: string, placeholder = ""): string {
  return `<input name="${name}" value="${esc(value)}" placeholder="${esc(placeholder)}">`;
}

/** The five product types of Ange.Bot; an older free text stays selectable until it is changed. */
function produktartSelect(value: string | null): string {
  const v = value ?? "";
  const options = ["", ...PRODUKTARTEN, ...(v && !(PRODUKTARTEN as readonly string[]).includes(v) ? [v] : [])];
  return `<label><span>${FIELD_LABELS.produktart}</span><select name="produktart">${options.map((o) => `<option value="${esc(o)}" ${o === v ? "selected" : ""}>${o ? esc(o) : "nicht bekannt"}</option>`).join("")}</select></label>`;
}

/** Batch size: required for a product made that is not NEM; hidden for packaging only (data-pack inverted). */
function chargenvorgabeForm(c: Chargenvorgabe | undefined, pack: string): string {
  const made = pack === "data-pack" ? "data-made hidden" : "data-made";
  const num = (x: number | null | undefined) => (typeof x === "number" ? inputNumber(x) : "");
  const sel = (name: string, values: readonly string[], cur: string, empty: string) =>
    `<select name="${name}">${["", ...values].map((o) => `<option value="${esc(o)}" ${o === cur ? "selected" : ""}>${o ? esc(o) : empty}</option>`).join("")}</select>`;
  const vorgegeben = c?.vorgegeben === true ? "ja" : c?.vorgegeben === false ? "nein" : "";
  return `<label ${made}><span>Chargengröße vorgegeben</span>${sel("cvVorgegeben", ["ja", "nein"], vorgegeben, "nicht bekannt")}</label>
    <label ${made}><span>Je Charge mindestens</span>${input("cvMin", num(c?.min), "Zahl")}</label>
    <label ${made}><span>Je Charge höchstens</span>${input("cvMax", num(c?.max), "Zahl")}</label>
    <label ${made}><span>Einheit der Charge</span>${sel("cvEinheit", ["kg", "Stück"], c?.einheit ?? "", "–")}</label>
    <label ${made}><span>Chargen je Kampagne</span>${input("cvKampagne", num(c?.chargenJeKampagne), "optional")}</label>
    <label ${made}><span>Grundlage</span>${sel("cvQuelle", ["Zulassung", "Kundenvorgabe", "Prozessvalidierung"], c?.quelle ?? "", "–")}</label>
    <label class="wide" ${made}><span>Beleg (Zitat oder Quelle, geht an Ange.Bot)</span>${input("cvBeleg", c ? belegOf(c) : "", "z. B. „Charge 500 kg laut Zulassung“")}</label>`;
}

function positionForm(p: Position, i: number): string {
  const subs = p.substanzen.map((s) => [s.name ?? "", typeof s.dose === "number" ? inputNumber(s.dose) : "", s.unit ?? ""].join("; ")).join("\n");
  const packOnly = leistungOf(p) === "nur_verpacken";
  // Shown only for "Nur verpacken"; the select switches them without redrawing the form (bindRequirements).
  const pack = packOnly ? "data-pack" : "data-pack hidden";
  const optional = `<span ${pack}> (Angabe, keine Pflicht)</span>`;
  const unit = p.portion?.unit ?? "Stück";
  return `<fieldset class="req-edit" data-pos="${i}"><legend>${input(`name`, p.name, "Position")}</legend>
    <label><span>${FIELD_LABELS.leistung}</span><select name="leistung">${(["herstellen", "nur_verpacken"] as const).map((l) => `<option value="${l}" ${l === leistungOf(p) ? "selected" : ""}>${LEISTUNG_TXT[l]}</option>`).join("")}</select></label>
    <label><span>${FIELD_LABELS.darreichungsform}${optional}</span>${input("darreichungsform", p.darreichungsform.value ?? "", "z. B. Hartkapsel")}</label>
    <label class="wide"><span>Substanzen, je Zeile „Name; Dosis; Einheit“${optional}</span><textarea name="substanzen" rows="4">${esc(subs)}</textarea></label>
    <label><span>Menge<span ${pack}> der Ware des Kunden</span></span>${input("menge", typeof p.menge.amount === "number" ? inputNumber(p.menge.amount) : "", "Zahl")}</label>
    <label><span>Einheit</span>${input("einheit", p.menge.unit ?? "", "Stück oder kg")}</label>
    <label ${pack}><span>${FIELD_LABELS.portion}</span>${input("portion", typeof p.portion?.amount === "number" ? inputNumber(p.portion.amount) : "", "z. B. 60")}</label>
    <label ${pack}><span>Einheit der Portion</span><select name="portionEinheit">${PORTION_UNITS.map((u) => `<option ${u === unit ? "selected" : ""}>${u}</option>`).join("")}</select></label>
    <label><span>${FIELD_LABELS.vpe}</span>${input("vpe", vpeValid(p.vpe?.value) ? String(p.vpe!.value) : "", "z. B. 30")}</label>
    <label><span>${FIELD_LABELS.mengenart}</span><select name="mengenart">${["", ...MENGENARTEN].map((m) => `<option value="${m}" ${m === (p.mengenart?.value ?? "") ? "selected" : ""}>${m ? MENGENART_TXT[m as Mengenart] : "nicht bekannt"}</option>`).join("")}</select></label>
    ${OPTIONAL_FIELDS.map((f) => (f === "produktart" ? produktartSelect(p.produktart.value) : `<label${f === "lieferstruktur" ? ' class="wide"' : ""}><span>${FIELD_LABELS[f]}</span>${input(f, p[f]?.value ?? "", f === "lieferstruktur" ? "z. B. jährlich zwei Lieferungen zu je 50.000 Packungen" : "")}</label>`)).join("")}
    ${asksChargenvorgabe() ? chargenvorgabeForm(p.chargenvorgabe, pack) : ""}
  </fieldset>`;
}


export function requirementsSection(d: Deal): string {
  const r = S.dealRequirements;
  const relevant = d.stage === "klaerung" || d.stage === "ange_bot" || !!r;
  if (!relevant) return "";
  const ro = S.readOnly;
  const head = `<h3>Anfrage für Ange.Bot <small>${S.dealRequirementsAt ? `Stand ${fmtDT(S.dealRequirementsAt)}` : "noch nicht geprüft"}</small></h3>`;
  const buttons = `<div class="actions">${ro ? "" : `<button class="btn" data-req-refresh>${r ? "Neu prüfen" : "Jetzt prüfen"}</button>`}${r && !ro && !editing ? `<button class="btn" data-req-edit>Korrigieren</button>` : ""}
    ${pushControls()}</div>${angeBotBlock()}`;
  if (!r) return `<div class="sec req">${head}<p class="muted" style="margin:0 0 8px">Die KI liest Mails, Anhänge und Notizen des Deals und trägt die Pflichtangaben für Ange.Bot mit Beleg zusammen.</p>${buttons}</div>`;
  if (editing) {
    return `<div class="sec req">${head}<form id="req-form" class="req-form">${r.positions.map(positionForm).join("")}
      <div class="actions"><button class="btn primary">Speichern</button><button class="btn" type="button" data-req-add>Position hinzufügen</button><button class="btn" type="button" data-req-cancel>Abbrechen</button></div></form></div>`;
  }
  const ready = readyForAngeBot(r, rules());
  return `<div class="sec req">${head}
    <div class="req-status">${ready ? `<span class="pill p-ok">Bereit für Ange.Bot</span>` : r.positions.length ? `<span class="pill p-hoch">Pflichtangaben fehlen oder sind zu klären</span>` : `<span class="pill p-info">Noch kein Produkt erkennbar</span>`}</div>
    ${r.positions.map((p, i) => positionView(p, i)).join("")}
    ${r.note ? `<p class="muted" style="margin:6px 0">${esc(r.note)}</p>` : ""}
    ${!ready && r.rueckfrage ? `<div class="draft"><label for="req-draft">Entwurf Rückfrage an den Kunden</label><textarea id="req-draft" readonly>${esc(r.rueckfrage)}</textarea><button class="btn" data-req-copy>Kopieren</button></div>` : ""}
    ${buttons}</div>`;
}

const PUSH_STATUS: Record<string, string> = { queued: "läuft", accepted: "angenommen", rejected: "abgewiesen", failed: "fehlgeschlagen" };

/** Push button with the choice of offer; why it is off, if it is. */
function pushControls(): string {
  const a = S.dealAngeBot;
  const pending = a?.pushes.some((p) => p.status === "queued");
  // Missing essentials do not lock the button: the confirmation asks for a written reason (operator 03.11.2026).
  const why = !a
    ? "Stand von Ange.Bot nicht geladen."
    : !a.enabled
      ? "Der Push ist noch nicht freigeschaltet; Ange.Bot baut gerade seinen Eingang."
      : a.gesperrt.length
        ? `So nimmt Ange.Bot den Push nicht an: ${a.gesperrt.join(" · ")}`
        : a.fehlend.length && !a.mitGrund
          ? `Es fehlen Pflichtangaben (${a.fehlend.join(" · ")}). Bitte nur sauber aufbereitete Deals pushen; Ange.Bot nimmt unvollständige Pushes noch nicht an.`
          : pending
          ? "Ein Push läuft gerade."
          : "";
  const off = S.readOnly || !!why;
  return `${machbarkeitButton()}<select id="push-art" aria-label="Angebotsart" ${off ? "disabled" : ""}><option value="indikativ">indikatives Angebot</option><option value="final">finales Angebot</option></select>
    <button class="btn primary" data-push ${off ? "disabled" : ""} title="${esc(why || (a?.fehlend.length ? "Es fehlen Pflichtangaben; ein Push geht nur mit schriftlichem Grund." : "Übergibt die Positionen an Ange.Bot; der Deal wechselt in die Phase Ange.Bot."))}">Push nach Ange.Bot</button>`;
}

/** "Machbarkeit prüfen", once Ange.Bot takes it: only complete positions are checked. */
function machbarkeitButton(): string {
  const m = S.dealAngeBot?.machbarkeit;
  if (!m?.enabled) return "";
  const r = rules();
  const complete = (S.dealRequirements?.positions ?? []).some((p) => completeForAngeBot(p, r));
  const why = m.pending ? "Eine Machbarkeitsprüfung läuft gerade." : !complete ? "Keine Position hat alle Pflichtangaben; geprüft werden nur vollständige Positionen." : "";
  return `<button class="btn" data-machbarkeit ${S.readOnly || why ? "disabled" : ""} title="${esc(why || "Ange.Bot beurteilt die vollständigen Positionen (bis zu drei Minuten); läuft auch von selbst, wenn sich eine Position ändert.")}">Machbarkeit prüfen</button>`;
}

/** What came back from Ange.Bot: last push (number or findings), offer, questions, state of the positions. */
// Packaging only (F13): form and substances are no part of the identity there, Ange.Bot names their change as a hint.
const FELD_LABELS: Record<string, string> = {
  produktname: "Produktname",
  regtyp: "Produktart",
  verpackung_roh: "Verpackung",
  menge: "Menge",
  kundenanforderungen: "Kundenanforderungen",
  darreichungsform: "Darreichungsform",
  form_spec: "Form-Spezifikation",
  substanzen: "Substanzen",
  portion: "Portion je Gebinde",
};

/** A finding of "angenommen mit Hinweis" (F4): a changed field of a position, or a position the push no longer has. */
function hinweisItem(h: AngeBotState["pushes"][number]["hinweise"][number]): string {
  const name = h.produktname ?? (h.position !== null ? `Position ${h.position + 1}` : "Position");
  const what =
    h.art === "weggefallen"
      ? "weggefallen"
      : h.art === "geaendert"
        ? `${h.feld ? (FELD_LABELS[h.feld] ?? (/^substanzen\[\d+\]\.funktion$/.test(h.feld) ? "Funktion einer Substanz" : h.feld)) : "Angabe"} geändert`
        : "Hinweis";
  return `<li>${esc(name)}: ${esc(what)}${h.text ? ` – ${esc(h.text)}` : ""}</li>`;
}

/** A position Ange.Bot did not make but holds as a Prüfkarte, with what it misses. */
function pruefkarteItem(k: AngeBotState["pushes"][number]["pruefkarten"][number]): string {
  return `<li>Position ${k.position + 1}${k.name ? ` (${esc(k.name)})` : ""}${k.fehlt.length ? `: ${esc(k.fehlt.join(" · "))}` : ""}</li>`;
}

/** „angenommen mit Hinweis“, „… mit Prüfkarte“ or both; null when the push was not accepted. */
function acceptedLabel(p: AngeBotState["pushes"][number]): string | null {
  if (p.status !== "accepted") return null;
  const mit = [p.hinweise.length ? "Hinweis" : "", p.pruefkarten.length ? (p.pruefkarten.length === 1 ? "Prüfkarte" : "Prüfkarten") : ""].filter(Boolean);
  return mit.length ? `angenommen mit ${mit.join(" und ")}` : null;
}

/** The running or failed feasibility check; the verdicts stand at their positions. */
function machbarkeitStatus(): string {
  const m = S.dealAngeBot?.machbarkeit;
  if (!m?.enabled) return "";
  if (m.pending) return `<p class="muted">Ange.Bot prüft die Machbarkeit (bis zu drei Minuten) …</p>`;
  const l = m.last;
  if (l?.status !== "failed") return "";
  return `<p class="note warn">Machbarkeitsprüfung (${fmtDT(l.createdAt)}${l.by ? `, ${esc(l.by)}` : l.trigger === "auto" ? ", automatisch" : ""}) nicht gelungen: ${esc(l.error ?? "")}</p>
    ${l.maengel.length ? `<ul class="ab-maengel">${l.maengel.map((x) => `<li>${x.position !== null ? `Position ${x.position + 1}: ` : ""}${esc(x.text)}</li>`).join("")}</ul>` : ""}`;
}

function angeBotBlock(): string {
  const a = S.dealAngeBot;
  const check = machbarkeitStatus();
  if (!a || (!a.pushes.length && !a.events.length && !a.flags.length && !check)) return "";
  const last = a.pushes[0];
  const latest = (typ: string) => a.events.find((e) => e.typ === typ);
  const offer = latest("angebot")?.daten as { angebot_nr?: string; status?: string; art?: string; summe?: { wert: number; waehrung: string } | null; alternativen?: boolean } | undefined;
  // Ange.Bot sends a fixed status code and its own wording (F14); the wording is shown, the code when it is missing.
  const stand = (latest("stand")?.daten as { positionen?: { position_nr?: string; bezeichnung?: string; status?: string; status_text?: string; hinweis?: string }[] } | undefined)?.positionen ?? [];
  const questions = a.events.filter((e) => e.typ === "rueckfrage").slice(0, 3);
  return `<div class="ab">
    ${check}
    ${a.flags.length ? `<p class="note warn">Achtung, eigene Regeln: ${esc(a.flags.join("; "))}</p>` : ""}
    ${
      last
        ? `<p class="ab-push">Letzter Push (${last.art === "final" ? "final" : "indikativ"}, ${fmtDT(last.createdAt)}${last.by ? `, ${esc(last.by)}` : ""}): <b>${esc(acceptedLabel(last) ?? PUSH_STATUS[last.status] ?? last.status)}</b>${last.anfrageNr ? ` · <span class="mono">${esc(last.anfrageNr)}</span>` : ""}${last.error ? ` · ${esc(last.error)}` : ""}</p>
          ${last.grund ? `<p class="ab-grund"><b>Trotz fehlender Angaben gepusht${last.by ? ` von ${esc(last.by)}` : ""}:</b> „${esc(last.grund)}“</p>` : ""}
          ${last.pruefkarten.length ? `<p class="muted" style="margin:4px 0 0">Bei Ange.Bot als Prüfkarte angelegt: Die Position entsteht dort erst, wenn jemand die Werte ergänzt und übernimmt.</p><ul class="ab-maengel">${last.pruefkarten.map(pruefkarteItem).join("")}</ul>` : ""}
          ${last.maengel.length ? `<ul class="ab-maengel">${last.maengel.map((m) => `<li>${m.position !== null ? `Position ${m.position + 1}: ` : ""}${esc(m.text)}</li>`).join("")}</ul>` : ""}
          ${last.hinweise.length ? `<p class="muted" style="margin:4px 0 0">Ange.Bot hat diese Positionen nicht geändert; die Hinweise stehen als Befund an der Anfrage.</p><ul class="ab-maengel">${last.hinweise.map(hinweisItem).join("")}</ul>` : ""}`
        : ""
    }
    ${offer ? `<p>Angebot <span class="mono">${esc(offer.angebot_nr ?? "")}</span>: ${esc(offer.status ?? "")}${offer.art ? `, ${esc(offer.art)}` : ""}${offer.summe ? `, ${offer.summe.wert.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${esc(offer.summe.waehrung)}` : offer.alternativen ? ", Alternativangebot ohne Gesamtsumme" : ""}</p>` : ""}
    ${questions.length ? `<p class="eyebrow">Rückfragen von Ange.Bot</p><ul class="ab-q">${questions.map((q) => `<li><span class="mono">${fmtDate(q.zeitpunkt)}</span> ${esc(String((q.daten as { text?: string }).text ?? ""))}</li>`).join("")}</ul>` : ""}
    ${stand.length ? `<p class="eyebrow">Stand in Ange.Bot</p><ul class="ab-q">${stand.map((p) => `<li>${esc(p.bezeichnung ?? p.position_nr ?? "Position")}: <b>${esc(p.status_text || p.status || "")}</b>${p.hinweis ? ` · ${esc(p.hinweis)}` : ""}</li>`).join("")}</ul>` : ""}
  </div>`;
}

/** Form → requirements; changed fields become manual so a new extraction keeps them. */
export function fromForm(form: HTMLFormElement, before: Requirements): Requirements {
  const positions = [...form.querySelectorAll<HTMLFieldSetElement>("fieldset[data-pos]")].map((fs) => {
    const old = before.positions[Number(fs.dataset.pos)];
    const get = (n: string) => (fs.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${n}"]`)?.value ?? "").trim();
    const field = (n: "darreichungsform" | (typeof OPTIONAL_FIELDS)[number]) => {
      const v = get(n) || null;
      const prev = old?.[n];
      if (prev && (prev.value ?? null) === v) return prev;
      // A field the position never had stays empty instead of an empty correction that would hold off the next reading.
      return !prev && v === null ? { value: null, evidence: null } : { value: v, evidence: null, manual: true };
    };
    const substanzen = get("substanzen")
      .split("\n")
      .map((l) => l.split(";").map((x) => x.trim()))
      .filter((p) => p[0])
      .map(([name, dose, unit]) => {
        const d = parseDeNumber(dose ?? "");
        const prev = old?.substanzen.find((s) => s.name === name && s.dose === d && s.unit === (unit || null));
        return prev ?? { name: name!, dose: Number.isFinite(d) && d > 0 ? d : null, unit: unit || null, evidence: null };
      });
    const subsChanged = JSON.stringify(substanzen) !== JSON.stringify(old?.substanzen ?? []);
    const amount = parseDeNumber(get("menge"));
    const menge = { amount: Number.isFinite(amount) && amount > 0 ? amount : null, unit: get("einheit") || null };
    const mengeSame = old && old.menge.amount === menge.amount && (old.menge.unit ?? null) === menge.unit;
    const leistung = get("leistung") === "nur_verpacken" ? "nur_verpacken" : "herstellen";
    // A position stored without a service is herstellen: keeping that is no change.
    const leistungField = old && leistungOf(old) === leistung ? old.leistung : { value: leistung, evidence: null, manual: true };
    // The portion only with "Nur verpacken"; a unit picked without a number is no value yet.
    const portionAmount = parseDeNumber(get("portion"));
    const portion = { amount: Number.isFinite(portionAmount) && portionAmount > 0 ? portionAmount : null, unit: get("portionEinheit") || null };
    const portionSame = (old?.portion?.amount ?? null) === portion.amount && (portion.amount === null || old?.portion?.unit === portion.unit);
    const portionField = portionSame ? old?.portion : { ...portion, evidence: null, manual: true };
    const chargenvorgabe = chargenvorgabeFrom(fs, old?.chargenvorgabe);
    // The quantity type: unchanged stays as read, a choice is a correction, never picked stays away.
    const art = (MENGENARTEN as readonly string[]).includes(get("mengenart")) ? (get("mengenart") as Mengenart) : null;
    const mengenart = (old?.mengenart?.value ?? null) === art ? old?.mengenart : { value: art, evidence: null, manual: true };
    // VPE: a whole number from 1 to 1,000,000; anything else counts as empty.
    const vpeNum = parseDeNumber(get("vpe"));
    const vpeValue = vpeValid(vpeNum) ? vpeNum : null;
    const vpe = (old?.vpe?.value ?? null) === vpeValue ? old?.vpe : { value: vpeValue, evidence: null, manual: true };
    return {
      name: get("name") || old?.name || "Position",
      ...(leistungField && { leistung: leistungField }),
      darreichungsform: field("darreichungsform"),
      substanzen,
      ...((subsChanged || old?.substanzenManual) && { substanzenManual: true }),
      menge: mengeSame ? old.menge : { ...menge, evidence: null, manual: true },
      ...(leistung === "nur_verpacken" && portionField && { portion: portionField }),
      lieferform: field("lieferform"),
      chargen: field("chargen"),
      lieferstruktur: field("lieferstruktur"),
      produktart: field("produktart"),
      formSpezifikation: field("formSpezifikation"),
      verpackung: field("verpackung"),
      ...(chargenvorgabe && { chargenvorgabe }),
      ...(mengenart && { mengenart }),
      ...(vpe && { vpe }),
    } as Position;
  });
  return { ...before, positions };
}

/**
 * The batch size from the form: unchanged keeps the AI's value with its source, a change is a human value with the
 * typed evidence. undefined: the form has no such fields (switched off), or nothing was ever entered.
 */
export function chargenvorgabeFrom(fs: HTMLElement, old: Chargenvorgabe | undefined): Chargenvorgabe | undefined {
  const el = (n: string) => fs.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${n}"]`);
  if (!el("cvVorgegeben")) return old;
  const get = (n: string) => (el(n)?.value ?? "").trim();
  const num = (n: string) => {
    const x = parseDeNumber(get(n));
    return Number.isFinite(x) && get(n) !== "" ? x : null;
  };
  const v = get("cvVorgegeben");
  const vorgegeben = v === "ja" ? true : v === "nein" ? false : null;
  const einheit = get("cvEinheit");
  const quelle = get("cvQuelle");
  const next = {
    vorgegeben,
    min: vorgegeben ? num("cvMin") : null,
    max: vorgegeben ? num("cvMax") : null,
    einheit: vorgegeben && (einheit === "kg" || einheit === "Stück") ? einheit : null,
    chargenJeKampagne: vorgegeben ? num("cvKampagne") : null,
    quelle: vorgegeben && (quelle === "Zulassung" || quelle === "Kundenvorgabe" || quelle === "Prozessvalidierung") ? quelle : null,
  } as const;
  const beleg = get("cvBeleg") || null;
  const same = (c: Chargenvorgabe) =>
    c.vorgegeben === next.vorgegeben &&
    (c.min ?? null) === next.min &&
    (c.max ?? null) === next.max &&
    (c.einheit ?? null) === next.einheit &&
    (c.chargenJeKampagne ?? null) === next.chargenJeKampagne &&
    (c.quelle ?? null) === next.quelle &&
    belegOf(c) === (beleg ?? "");
  if (old && same(old)) return old;
  if (!old && vorgegeben === null) return undefined;
  return { ...next, evidence: null, beleg, manual: true };
}

const emptyPosition = (): Position => {
  const none = { value: null, evidence: null };
  return { name: "Neue Position", darreichungsform: none, substanzen: [], menge: { amount: null, unit: null, evidence: null }, lieferform: none, chargen: none, produktart: none, formSpezifikation: none, verpackung: none };
};

export function bindRequirements(dealOf: () => Deal | undefined): void {
  document.addEventListener("click", async (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>(
      "#drawer [data-req-refresh], #drawer [data-req-edit], #drawer [data-req-cancel], #drawer [data-req-add], #drawer [data-req-copy], #drawer [data-ev-mail], #drawer [data-ev-att], #drawer [data-push], #drawer [data-machbarkeit]",
    );
    if (!t) return;
    const d = dealOf();
    if (!d) return;
    if (t.dataset.evMail) return void showOriginal(t.dataset.evMail);
    if (t.dataset.evAtt) return void downloadAttachment(t.dataset.evAtt, t.dataset.name ?? "anhang");
    if (t.dataset.reqCopy !== undefined) {
      await navigator.clipboard.writeText(S.dealRequirements?.rueckfrage ?? "").catch(() => undefined);
      return toast("Entwurf kopiert");
    }
    if (t.dataset.push !== undefined) {
      const art = (document.querySelector<HTMLSelectElement>("#push-art")?.value ?? "indikativ") as "indikativ" | "final";
      // First what would go out, then one more click (operator 02.11.2026).
      const preview = await S.api.pushPreview(d.id, art).catch(() => null);
      if (!preview) return toast("Die Vorschau des Pushs konnte nicht geladen werden; es wurde nichts gesendet.");
      const grund = await confirmPush(preview, S.dealAngeBot?.fehlend ?? []);
      if (grund === null) return;
      const ok = await write(() => S.api.pushAngeBot(d.id, art, grund || undefined), `Push nach Ange.Bot (${art}) läuft; die Antwort erscheint hier in wenigen Sekunden.`);
      await loadDealDetail().catch(() => undefined);
      render();
      if (ok) void followPush(d.id);
      return;
    }
    if (t.dataset.machbarkeit !== undefined) {
      const ok = await write(() => S.api.checkMachbarkeit(d.id), "Ange.Bot prüft die Machbarkeit; das Ergebnis erscheint hier in bis zu drei Minuten.");
      await loadDealDetail().catch(() => undefined);
      render();
      if (ok) void followMachbarkeit(d.id);
      return;
    }
    if (t.dataset.reqRefresh !== undefined) {
      return void write(() => S.api.refreshRequirements(d.id), "Die Prüfung läuft im Hintergrund; das Ergebnis erscheint in etwa einer Minute.");
    }
    if (t.dataset.reqEdit !== undefined) {
      editing = true;
      const d = dealOf();
      editBase = d && S.dealRequirements ? { dealId: d.id, version: d.version, requirements: S.dealRequirements } : null;
    }
    if (t.dataset.reqCancel !== undefined) editing = false;
    if (t.dataset.reqAdd !== undefined && S.dealRequirements) S.dealRequirements = { ...S.dealRequirements, positions: [...S.dealRequirements.positions, emptyPosition()] };
    render();
  });
  // "Nur verpacken" shows the portion and hides the batch size in place: a redraw would drop what has been typed.
  document.addEventListener("change", (e) => {
    const s = e.target as HTMLSelectElement;
    if (!s.matches('#req-form select[name="leistung"]')) return;
    for (const el of s.closest("fieldset")?.querySelectorAll<HTMLElement>("[data-pack]") ?? []) el.hidden = s.value !== "nur_verpacken";
    for (const el of s.closest("fieldset")?.querySelectorAll<HTMLElement>("[data-made]") ?? []) el.hidden = s.value === "nur_verpacken";
  });
  document.addEventListener("submit", async (e) => {
    const f = e.target as HTMLFormElement;
    if (f.id !== "req-form") return;
    e.preventDefault();
    const d = dealOf();
    if (!d || !editBase || editBase.dealId !== d.id) return;
    const base = editBase;
    const body = fromForm(f, base.requirements);
    const ok = await write(() => S.api.saveRequirements(d.id, base.version, body), "Pflichtangaben gespeichert; deine Werte bleiben bei einer neuen Prüfung erhalten.");
    if (ok) {
      editing = false;
      editBase = null;
    }
    await loadDealDetail().catch(() => undefined);
    render();
  });
}

/** Asks for the state while the check runs (Ange.Bot takes up to three minutes), as long as the deal stays open. */
/** What came of a push, for the message after the click. */
export function pushOutcome(p: AngeBotState["pushes"][number]): string {
  if (p.status === "accepted") {
    const karten = p.pruefkarten.length ? `, ${p.pruefkarten.length === 1 ? "eine Position" : `${p.pruefkarten.length} Positionen`} als Prüfkarte` : "";
    return `Ange.Bot hat angenommen${p.anfrageNr ? `: ${p.anfrageNr}` : ""}${p.hinweise.length ? ", mit Hinweis" : ""}${karten}.`;
  }
  if (p.status === "rejected") return `Ange.Bot hat den Push abgewiesen (${p.maengel.length === 1 ? "ein Mangel" : `${p.maengel.length} Mängel`}); die Gründe stehen beim Deal.`;
  return p.error ?? "Der Push ist fehlgeschlagen.";
}

/** After "Push nach Ange.Bot": watches the push until Ange.Bot answered and says what came of it (operator 02.11.2026). */
async function followPush(dealId: string): Promise<void> {
  for (let i = 0; i < 40; i++) {
    await new Promise((r) => setTimeout(r, 3000));
    if (S.openId !== dealId) return;
    let a: AngeBotState;
    try {
      a = await S.api.angeBot(dealId);
    } catch {
      continue;
    }
    if (S.openId !== dealId) return;
    S.dealAngeBot = a;
    render();
    const last = a.pushes[0];
    if (!last || last.status === "queued") continue;
    toast(pushOutcome(last), last.status === "accepted" ? undefined : 10_000);
    // A failure is in the bell now.
    if (last.status !== "accepted") S.meta = await S.api.meta().catch(() => S.meta);
    return render();
  }
}

async function followMachbarkeit(dealId: string): Promise<void> {
  for (let i = 0; i < 48; i++) {
    await new Promise((r) => setTimeout(r, 5000));
    if (S.openId !== dealId) return;
    try {
      S.dealAngeBot = await S.api.angeBot(dealId);
    } catch {
      continue;
    }
    render();
    if (!S.dealAngeBot.machbarkeit.pending) return;
  }
}
